import { useState, useEffect } from "react";
import { api } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Star, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "../utils/formatters";

const TimeEntriesPage = () => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [formData, setFormData] = useState({
    employee_id: "",
    employee_name: "",
    project_name: "",
    hours: "",
    hiring_manager: "",
    hourly_pay_rate: "",
    payment_date: "",
    payment_method: "cash",
    rating: "5",
    comments: "",
  });

  // Helper function to format date correctly (avoid timezone issues)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // For date-only strings (YYYY-MM-DD), display as-is without timezone conversion
    if (dateString.includes('T')) {
      // Has time component, use normal conversion
      return new Date(dateString).toLocaleDateString();
    } else {
      // Date only, parse and display without timezone shift
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, employeesRes, projectsRes] = await Promise.all([
        api.get("/time-entries"),
        api.get("/employees"),
        api.get("/projects"),
      ]);
      setEntries(entriesRes.data);
      setEmployees(employeesRes.data.filter(emp => emp.status === "active"));
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        employee_id: entry.employee_id,
        employee_name: entry.employee_name,
        project_name: entry.project_name,
        hours: entry.hours.toString(),
        hiring_manager: entry.hiring_manager,
        hourly_pay_rate: entry.hourly_pay_rate.toString(),
        payment_date: entry.payment_date,
        payment_method: entry.payment_method,
        rating: entry.rating.toString(),
        comments: entry.comments || "",
      });
    } else {
      setEditingEntry(null);
      setFormData({
        employee_id: "",
        employee_name: "",
        project_name: "",
        hours: "",
        hiring_manager: "",
        hourly_pay_rate: "",
        payment_date: "",
        payment_method: "cash",
        rating: "5",
        comments: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    if (employee) {
      setFormData({
        ...formData,
        employee_id: employee.id,
        employee_name: employee.name,
        hourly_pay_rate: employee.hourly_rate.toString(),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        hours: parseFloat(formData.hours),
        hourly_pay_rate: parseFloat(formData.hourly_pay_rate),
        rating: parseInt(formData.rating),
      };

      if (editingEntry) {
        await api.put(`/time-entries/${editingEntry.id}`, payload);
        toast.success("Time entry updated successfully");
      } else {
        await api.post("/time-entries", payload);
        toast.success("Time entry created successfully");
      }

      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving time entry:", error);
      toast.error("Failed to save time entry");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this time entry?")) {
      return;
    }

    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success("Time entry deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting time entry:", error);
      toast.error("Failed to delete time entry");
    }
  };

  const handleExportCSV = () => {
    // Use the same filtering logic as the display
    const exportEntries = (() => {
      if (filterEmployee === "all") return entries;
      if (filterEmployee === "owner") {
        return entries.filter(e => {
          const emp = employees.find(emp => emp.id === e.employee_id);
          return emp && emp.type === "Owner";
        });
      }
      if (filterEmployee === "employee") {
        return entries.filter(e => {
          const emp = employees.find(emp => emp.id === e.employee_id);
          return emp && emp.type !== "Owner";
        });
      }
      return entries.filter(e => e.employee_id === filterEmployee);
    })();

    const headers = [
      "Employee Name",
      "Project",
      "Hours",
      "Hiring Manager",
      "Pay Rate",
      "Total Payment",
      "Payment Date",
      "Payment Method",
      "Rating",
      "Comments"
    ];

    const rows = exportEntries.map(entry => [
      entry.employee_name,
      entry.project_name,
      entry.hours,
      entry.hiring_manager,
      `$${entry.hourly_pay_rate}`,
      `$${(entry.hours * entry.hourly_pay_rate).toFixed(2)}`,
      formatDate(entry.payment_date),
      entry.payment_method,
      entry.rating,
      entry.comments || ""
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `time_entries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Report exported successfully");
  };

  // Filter entries based on selected filter
  const filteredEntries = (() => {
    if (filterEmployee === "all") return entries;
    if (filterEmployee === "owner") {
      return entries.filter(e => {
        const emp = employees.find(emp => emp.id === e.employee_id);
        return emp && emp.type === "Owner";
      });
    }
    if (filterEmployee === "employee") {
      return entries.filter(e => {
        const emp = employees.find(emp => emp.id === e.employee_id);
        return emp && emp.type !== "Owner";
      });
    }
    return entries.filter(e => e.employee_id === filterEmployee);
  })();
  
  // Calculate subtotals based on filtered entries
  const totalHours = filteredEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  
  const ownerTotal = filteredEntries
    .filter(e => {
      const emp = employees.find(emp => emp.id === e.employee_id);
      return emp && emp.type === "Owner";
    })
    .reduce((sum, e) => sum + (e.hours * e.hourly_pay_rate), 0);
  
  const employeeTotal = filteredEntries
    .filter(e => {
      const emp = employees.find(emp => emp.id === e.employee_id);
      return emp && emp.type !== "Owner";
    })
    .reduce((sum, e) => sum + (e.hours * e.hourly_pay_rate), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-slate-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 page-enter" data-testid="time-entries-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Time Entries</h1>
          <p className="text-slate-600 mt-1">Track hours and manage payments</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="border-slate-300"
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
            data-testid="add-time-entry-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Filter */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-slate-400" />
            <Label htmlFor="filter" className="text-sm font-medium">Filter by:</Label>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-[250px]" data-testid="filter-employee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                <SelectItem value="owner">Owner Only</SelectItem>
                <SelectItem value="employee">Employees Only</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} {emp.type === "Owner" ? "(Owner)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterEmployee !== "all" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilterEmployee("all")}
                data-testid="clear-filter-button"
              >
                Clear Filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>All Time Entries ({filteredEntries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Pay Rate</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      No time entries found. Add your first entry to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`time-entry-row-${entry.id}`}>
                      <TableCell className="font-medium">{entry.employee_name}</TableCell>
                      <TableCell>{entry.project_name}</TableCell>
                      <TableCell>{entry.hours}h</TableCell>
                      <TableCell className="text-sm text-slate-600">{entry.hiring_manager}</TableCell>
                      <TableCell>{formatCurrency(entry.hourly_pay_rate)}/hr</TableCell>
                      <TableCell className="font-semibold text-emerald-600">
                        {formatCurrency(entry.hours * entry.hourly_pay_rate)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(entry.payment_date)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {entry.payment_method.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>{renderStars(entry.rating)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(entry)}
                            data-testid={`edit-time-entry-${entry.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-time-entry-${entry.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Subtotals */}
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Pay Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-xs text-purple-600 font-medium mb-1">Total Hours</div>
                <div className="text-2xl font-bold text-purple-700">
                  {totalHours.toFixed(2)}h
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-xs text-blue-600 font-medium mb-1">Owner Earnings</div>
                <div className="text-2xl font-bold text-blue-700">
                  {formatCurrency(ownerTotal)}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-xs text-slate-600 font-medium mb-1">Employee Labour Cost</div>
                <div className="text-2xl font-bold text-slate-700">
                  {formatCurrency(employeeTotal)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="time-entry-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Edit Time Entry" : "Add New Time Entry"}
            </DialogTitle>
            <DialogDescription>
              {editingEntry
                ? "Update time entry information"
                : "Record hours worked and payment details"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={handleEmployeeSelect}
                  disabled={editingEntry !== null}
                >
                  <SelectTrigger data-testid="time-entry-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} - ${emp.hourly_rate}/hr
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project_name">Project Name *</Label>
                <Select
                  value={formData.project_name}
                  onValueChange={(value) =>
                    setFormData({ ...formData, project_name: value })
                  }
                  required
                >
                  <SelectTrigger data-testid="time-entry-project-select">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length > 0 ? (
                      projects.map((project) => (
                        <SelectItem key={project.id} value={project.project_name}>
                          {project.project_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-projects" disabled>
                        No projects available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours Worked *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.5"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    required
                    data-testid="time-entry-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_pay_rate">Hourly Pay Rate ($) *</Label>
                  <Input
                    id="hourly_pay_rate"
                    type="number"
                    step="0.01"
                    value={formData.hourly_pay_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, hourly_pay_rate: e.target.value })
                    }
                    required
                    data-testid="time-entry-pay-rate-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hiring_manager">Hiring Manager *</Label>
                <Input
                  id="hiring_manager"
                  value={formData.hiring_manager}
                  onChange={(e) =>
                    setFormData({ ...formData, hiring_manager: e.target.value })
                  }
                  required
                  data-testid="time-entry-manager-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_date">Payment Date *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) =>
                      setFormData({ ...formData, payment_date: e.target.value })
                    }
                    required
                    data-testid="time-entry-payment-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Payment Method *</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) =>
                      setFormData({ ...formData, payment_method: value })
                    }
                  >
                    <SelectTrigger data-testid="time-entry-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="e-transfer">E-Transfer</SelectItem>
                      <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rating">Performance Rating (1-5) *</Label>
                <Select
                  value={formData.rating}
                  onValueChange={(value) => setFormData({ ...formData, rating: value })}
                >
                  <SelectTrigger data-testid="time-entry-rating-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">⭐⭐⭐⭐⭐ - Excellent</SelectItem>
                    <SelectItem value="4">⭐⭐⭐⭐ - Good</SelectItem>
                    <SelectItem value="3">⭐⭐⭐ - Fair</SelectItem>
                    <SelectItem value="2">⭐⭐ - Below Average</SelectItem>
                    <SelectItem value="1">⭐ - Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="comments">Comments / Feedback</Label>
                <Textarea
                  id="comments"
                  value={formData.comments}
                  onChange={(e) =>
                    setFormData({ ...formData, comments: e.target.value })
                  }
                  placeholder="Additional notes about performance..."
                  rows={3}
                  data-testid="time-entry-comments-input"
                />
              </div>

              {formData.hours && formData.hourly_pay_rate && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-emerald-900">
                      Total Payment:
                    </span>
                    <span className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(parseFloat(formData.hours) * parseFloat(formData.hourly_pay_rate))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="time-entry-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="time-entry-save-button"
              >
                {editingEntry ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeEntriesPage;
